import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { take } from 'rxjs/operators';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import Api from '../helpers/api';
import App from '../helpers/app';

(<any>pdfMake).vfs = pdfFonts.pdfMake.vfs;

interface Municipio {
  id: number;
  nome: string;
  uf?: string;
}

@Component({
  selector: 'app-lotes-conferencia',
  templateUrl: './lotes-conferencia.component.html',
  styleUrls: ['./lotes-conferencia.component.scss']
})
export class LotesConferenciaComponent implements OnInit {
  municipios: Municipio[] = [];
  municipiosLoading = false;
  municipiosOffset = 0;
  municipiosLimit = 50;
  municipiosHasMore = true;
  municipiosSearchTerm = '';

  lotes: any[] = [];
  loteSelecionado: any = null;
  conferencias: any[] = [];
  totalConferencias = 0;
  paginaConferencias = 1;
  tamanhoPagina = 20;

  novoLote: any = {
    id_cidade: null,
    nome: '',
    modo_distribuicao: 'FILA_LIVRE',
    observacoes: ''
  };
  filtroCidade: number = null;
  filtroStatus = '';
  buscaConferencia = '';
  situacaoConferencia = '';
  filtroConferidor: number = null;
  abrindo = false;
  carregandoLotes = false;
  carregandoConferencias = false;
  distribuicao: any = null;
  carregandoDistribuicao = false;
  salvandoDistribuicao = false;
  removendoDistribuicao = false;
  conferenciaPorLote = false;
  municipioConfiguracao: number = null;
  configuracaoCarregada = false;
  carregandoConfiguracao = false;
  salvandoConfiguracao = false;
  novoLoteConferenciaPorLote: boolean = null;
  carregandoConfiguracaoNovoLote = false;

  Math = Math;

  constructor(
    private api: Api,
    private app: App,
    private modalService: NgbModal,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await Promise.all([
      this.carregarMunicipios(),
      this.carregarLotes()
    ]);
    this.cdr.detectChanges();
  }

  abrirModalConfiguracao(content: any) {
    if (!this.municipioConfiguracao && this.filtroCidade) {
      this.municipioConfiguracao = Number(this.filtroCidade);
    }
    this.modalService.open(content, { centered: true });
    if (this.municipioConfiguracao) {
      this.carregarConfiguracao();
    }
  }

  async carregarConfiguracao() {
    if (!this.municipioConfiguracao) {
      this.conferenciaPorLote = false;
      this.configuracaoCarregada = false;
      this.carregandoConfiguracao = false;
      return;
    }
    const idCidade = Number(this.municipioConfiguracao);
    this.configuracaoCarregada = false;
    this.carregandoConfiguracao = true;
    try {
      const response = await this.requisicao(
        'get',
        `conferidores/configuracao-conferencia?id_cidade=${idCidade}`
      );
      if (Number(this.municipioConfiguracao) !== idCidade) return;
      this.conferenciaPorLote = response && response.conferencia_por_lote === true;
      this.configuracaoCarregada = true;
    } catch (error) {
      if (Number(this.municipioConfiguracao) !== idCidade) return;
      this.configuracaoCarregada = false;
      await this.app.alert(
        'Erro',
        error.message || 'Não foi possível carregar a configuração da conferência deste município.',
        'error'
      );
    } finally {
      if (Number(this.municipioConfiguracao) === idCidade) {
        this.carregandoConfiguracao = false;
      }
      this.cdr.detectChanges();
    }
  }

  async alterarConferenciaPorLote() {
    if (!this.municipioConfiguracao || !this.configuracaoCarregada) return;
    const habilitar = !this.conferenciaPorLote;
    const confirmou = await this.app.confirm(
      habilitar ? 'Ativar conferência por lote' : 'Desativar conferência por lote',
      habilitar
        ? 'A conferência passará a exigir lote aberto. Em cada lote você poderá escolher fila livre ou distribuição entre conferidores. Deseja continuar?'
        : 'Os conferidores voltarão a visualizar todos os servidores disponíveis do município, sem exigir lote aberto. Deseja continuar?',
      habilitar ? 'Ativar' : 'Desativar',
      'Cancelar'
    );
    if (!confirmou) return;

    this.salvandoConfiguracao = true;
    try {
      const response = await this.requisicao(
        'put',
        'conferidores/configuracao-conferencia',
        {
          id_cidade: this.municipioConfiguracao,
          conferencia_por_lote: habilitar
        }
      );
      this.conferenciaPorLote = response && response.conferencia_por_lote === true;
      await this.app.alert(
        'Configuração atualizada',
        this.conferenciaPorLote
          ? 'A conferência por lote está ativa neste município.'
          : 'A conferência sem lote está ativa neste município.',
        'success'
      );
    } catch (error) {
      await this.app.alert(
        'Erro',
        error.message || 'Não foi possível atualizar a configuração do município.',
        'error'
      );
    } finally {
      this.salvandoConfiguracao = false;
      this.cdr.detectChanges();
    }
  }

  get lotesAtivos(): number {
    return this.lotes.filter(item => item.status === 'aberto').length;
  }

  get lotesFechados(): number {
    return this.lotes.filter(item => item.status === 'fechado').length;
  }

  get progressoLoteAtivo(): number {
    const loteAtivo = this.lotes.find(item => item.status === 'aberto');
    return loteAtivo ? Number(loteAtivo.percentual_concluido || 0) : 0;
  }

  get conferenciasEmAberto(): number {
    return this.lotes
      .filter(item => item.status === 'aberto')
      .reduce(
        (total, item) => total + Number(item.pendentes || 0) + Number(item.em_conferencia || 0),
        0
      );
  }

  get conferenciasPendentesLote(): number {
    if (!this.loteSelecionado) return 0;
    return Number(this.loteSelecionado.pendentes || 0) + Number(this.loteSelecionado.em_conferencia || 0);
  }

  get progressoPorConferidor(): any[] {
    if (!this.distribuicao || !this.distribuicao.conferidores) return [];
    return this.distribuicao.conferidores
      .filter(item => Number(item.total_atribuido || 0) > 0)
      .map(item => {
        const total = Number(item.total_atribuido || 0);
        const conferidas = Number(item.conferidas || 0);
        return {
          ...item,
          total_atribuido: total,
          conferidas,
          percentual: total ? Math.round((conferidas * 10000) / total) / 100 : 0
        };
      });
  }

  private async requisicao(method: 'get' | 'post' | 'put' | 'delete', url: string, body?: any): Promise<any> {
    // Cada chamada usa seu próprio cliente para que requisições simultâneas
    // não compartilhem estado de loading/erro no helper legado de API.
    const apiCall = this.api.new().silent();
    const request = method === 'get'
      ? apiCall.get(url)
      : method === 'put'
        ? apiCall.put(url, body)
        : method === 'delete'
          ? apiCall.delete(url)
          : apiCall.post(url, body);
    const response = await request.pipe(take(1)).toPromise();
    if (response && response.status >= 400) {
      const message = response.error && response.error.message
        ? response.error.message
        : 'Não foi possível concluir a operação.';
      throw new Error(message);
    }
    return response;
  }

  async carregarMunicipios(nome: string = '', append: boolean = false) {
    this.municipiosLoading = true;
    try {
      const params: any = {
        limit: this.municipiosLimit,
        offset: append ? this.municipiosOffset : 0
      };
      if (nome.trim()) params.nome = nome.trim();

      const response = await this.api.new().silent().get('municipios', params).pipe(take(1)).toPromise();
      const novos = response && response.data
        ? response.data
        : (Array.isArray(response) ? response : []);

      this.municipios = append ? [...this.municipios, ...novos] : novos;
      if (!append) this.municipiosOffset = 0;
      this.municipiosOffset += novos.length;
      this.municipiosHasMore = novos.length === this.municipiosLimit;
    } finally {
      this.municipiosLoading = false;
      this.cdr.detectChanges();
    }
  }

  async onMunicipioSearch(event: { term: string }) {
    this.municipiosSearchTerm = event.term || '';
    this.municipiosOffset = 0;
    if (!event.term || event.term.length >= 2) {
      await this.carregarMunicipios(this.municipiosSearchTerm);
    }
  }

  async onMunicipioScrollToEnd() {
    if (!this.municipiosLoading && this.municipiosHasMore) {
      await this.carregarMunicipios(this.municipiosSearchTerm, true);
    }
  }

  async carregarLotes() {
    this.carregandoLotes = true;
    try {
      const params = new URLSearchParams();
      if (this.filtroCidade) params.set('id_cidade', String(this.filtroCidade));
      if (this.filtroStatus) params.set('status', this.filtroStatus);
      const response = await this.requisicao('get', 'lotes-conferencia' + (params.toString() ? '?' + params : ''));
      this.lotes = Array.isArray(response) ? response : [];

      if (this.loteSelecionado) {
        const atualizado = this.lotes.find(item => Number(item.id) === Number(this.loteSelecionado.id));
        if (atualizado) this.loteSelecionado = atualizado;
      }
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Erro ao carregar lotes.', 'error');
    } finally {
      this.carregandoLotes = false;
      this.cdr.detectChanges();
    }
  }

  abrirModalNovoLote(content: any) {
    this.novoLote = {
      id_cidade: null,
      nome: '',
      modo_distribuicao: 'FILA_LIVRE',
      observacoes: ''
    };
    this.novoLoteConferenciaPorLote = null;
    this.modalService.open(content, {
      backdrop: 'static',
      centered: true,
      keyboard: false,
      size: 'lg'
    });
  }

  async abrirLote(modal?: any) {
    if (!this.novoLote.id_cidade || !String(this.novoLote.nome || '').trim()) {
      await this.app.alert('Atenção', 'Informe o município e o nome do lote.', 'warning');
      return;
    }
    if (this.novoLoteConferenciaPorLote !== true) {
      await this.app.alert(
        'Conferência por lote desativada',
        'Ative a conferência por lote para o município selecionado antes de abrir o lote.',
        'warning'
      );
      return;
    }

    const municipio = this.municipios.find(item => Number(item.id) === Number(this.novoLote.id_cidade));
    const confirmou = await this.app.confirm(
      'Abrir lote de conferência',
      'Todos os vínculos aptos de ' + (municipio ? municipio.nome : 'este município') +
      ' entrarão como pendentes em uma nova rodada no modo ' +
      this.rotuloModo(this.novoLote.modo_distribuicao).toLowerCase() + '.',
      'Abrir lote'
    );
    if (!confirmou) return;

    this.abrindo = true;
    this.cdr.detectChanges();
    try {
      const response = await this.requisicao('post', 'lotes-conferencia/abrir', {
        id_cidade: this.novoLote.id_cidade,
        nome: String(this.novoLote.nome).trim(),
        modo_distribuicao: this.novoLote.modo_distribuicao,
        observacoes: String(this.novoLote.observacoes || '').trim() || null
      });
      this.novoLote = {
        id_cidade: null,
        nome: '',
        modo_distribuicao: 'FILA_LIVRE',
        observacoes: ''
      };
      this.novoLoteConferenciaPorLote = null;
      await this.carregarLotes();
      await this.selecionarLote(response.data);
      if (modal) modal.close('lote-aberto');
      await this.app.alert(
        'Lote aberto',
        response.data.modo_distribuicao === 'POR_META'
          ? 'A rodada foi criada. Faça a distribuição antes de os conferidores iniciarem o trabalho.'
          : 'A rodada foi criada em fila livre para os conferidores do município.',
        'success'
      );
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível abrir o lote.', 'error');
    } finally {
      this.abrindo = false;
      this.cdr.detectChanges();
    }
  }

  async onMunicipioNovoLoteChange() {
    this.novoLoteConferenciaPorLote = null;
    if (!this.novoLote.id_cidade) {
      this.carregandoConfiguracaoNovoLote = false;
      return;
    }
    const idCidade = Number(this.novoLote.id_cidade);
    this.carregandoConfiguracaoNovoLote = true;
    try {
      const response = await this.requisicao(
        'get',
        `conferidores/configuracao-conferencia?id_cidade=${idCidade}`
      );
      if (Number(this.novoLote.id_cidade) !== idCidade) return;
      this.novoLoteConferenciaPorLote = response && response.conferencia_por_lote === true;
    } catch (error) {
      if (Number(this.novoLote.id_cidade) !== idCidade) return;
      await this.app.alert(
        'Erro',
        error.message || 'Não foi possível consultar a configuração do município.',
        'error'
      );
    } finally {
      if (Number(this.novoLote.id_cidade) === idCidade) {
        this.carregandoConfiguracaoNovoLote = false;
      }
      this.cdr.detectChanges();
    }
  }

  async selecionarLote(lote: any) {
    this.loteSelecionado = lote;
    this.paginaConferencias = 1;
    this.buscaConferencia = '';
    this.situacaoConferencia = '';
    this.filtroConferidor = null;
    this.cdr.detectChanges();
    // A lista é o conteúdo principal da tela. Carregá-la primeiro evita que o
    // resumo concorrente deixe a tabela vazia até a primeira ação de filtro.
    await this.carregarConferencias();
    await this.carregarResumoConferidores();
    this.cdr.detectChanges();
  }

  voltarParaLotes() {
    this.loteSelecionado = null;
    this.conferencias = [];
    this.totalConferencias = 0;
    this.distribuicao = null;
  }

  async carregarResumoConferidores() {
    if (!this.loteSelecionado) return;
    try {
      this.distribuicao = await this.requisicao(
        'get',
        `lotes-conferencia/${this.loteSelecionado.id}/distribuicao`
      );
    } catch (error) {
      this.distribuicao = null;
      await this.app.alert('Erro', error.message || 'Erro ao carregar o progresso dos conferidores.', 'error');
    } finally {
      this.cdr.detectChanges();
    }
  }

  async carregarConferencias() {
    if (!this.loteSelecionado) return;
    this.carregandoConferencias = true;
    try {
      const params = new URLSearchParams();
      params.set('offset', String((this.paginaConferencias - 1) * this.tamanhoPagina));
      params.set('limit', String(this.tamanhoPagina));
      if (this.buscaConferencia.trim()) params.set('nome', this.buscaConferencia.trim());
      if (this.situacaoConferencia) params.set('situacao', this.situacaoConferencia);
      if (this.filtroConferidor) params.set('id_conferidor', String(this.filtroConferidor));

      const response = await this.requisicao(
        'get',
        `lotes-conferencia/${this.loteSelecionado.id}/conferencias?${params.toString()}`
      );
      this.conferencias = response.data || [];
      this.totalConferencias = Number(response.total || 0);
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Erro ao carregar conferências.', 'error');
    } finally {
      this.carregandoConferencias = false;
      this.cdr.detectChanges();
    }
  }

  async filtrarConferencias() {
    this.paginaConferencias = 1;
    await this.carregarConferencias();
  }

  async mudarPagina(delta: number) {
    const proxima = this.paginaConferencias + delta;
    const totalPaginas = Math.max(1, Math.ceil(this.totalConferencias / this.tamanhoPagina));
    if (proxima < 1 || proxima > totalPaginas) return;
    this.paginaConferencias = proxima;
    await this.carregarConferencias();
  }

  async abrirModalDistribuicao(content: any) {
    if (!this.loteSelecionado) return;
    this.carregandoDistribuicao = true;
    this.spinner.show();
    try {
      const response = await this.requisicao(
        'get',
        `lotes-conferencia/${this.loteSelecionado.id}/distribuicao`
      );
      this.distribuicao = response;
      this.modalService.open(content, {
        centered: true,
        size: 'lg',
        backdrop: 'static',
        keyboard: false
      });
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível carregar a distribuição.', 'error');
    } finally {
      this.carregandoDistribuicao = false;
      this.spinner.hide();
      this.cdr.detectChanges();
    }
  }

  get totalPlanejadoDistribuicao(): number {
    if (!this.distribuicao || !this.distribuicao.conferidores) return 0;
    return this.distribuicao.conferidores.reduce(
      (total, item) => total + (Number(item.quantidade_planejada) || 0),
      0
    );
  }

  get totalLoteDistribuicao(): number {
    return this.distribuicao && this.distribuicao.resumo
      ? Number(this.distribuicao.resumo.total_lote || 0)
      : 0;
  }

  get distribuicaoValida(): boolean {
    if (!this.distribuicao) return false;
    const quantidadesValidas = this.distribuicao.conferidores.every(item => {
      const quantidade = Number(item.quantidade_planejada);
      return Number.isInteger(quantidade) &&
        quantidade >= Number(item.quantidade_minima || 0);
    });
    return quantidadesValidas && this.totalPlanejadoDistribuicao === this.totalLoteDistribuicao;
  }

  dividirIgualmente() {
    if (!this.distribuicao) return;
    const disponiveis = this.distribuicao.conferidores.filter(item => item.disponivel);
    if (!disponiveis.length) {
      this.app.alert('Atenção', 'Não há conferidores ativos vinculados ao município.', 'warning');
      return;
    }

    this.distribuicao.conferidores.forEach(item => {
      item.quantidade_planejada = Number(item.quantidade_minima || 0);
    });
    let restante = this.totalLoteDistribuicao - this.totalPlanejadoDistribuicao;
    let indice = 0;
    while (restante > 0) {
      disponiveis[indice % disponiveis.length].quantidade_planejada += 1;
      indice += 1;
      restante -= 1;
    }
  }

  ajustarQuantidade(item: any) {
    const valor = Number(item.quantidade_planejada);
    item.quantidade_planejada = Number.isFinite(valor)
      ? Math.max(0, Math.floor(valor))
      : 0;
  }

  async salvarDistribuicao(modal: any) {
    if (!this.distribuicaoValida) {
      await this.app.alert(
        'Quantidades inválidas',
        `A soma deve ser exatamente ${this.totalLoteDistribuicao} servidores e nenhuma quantidade pode ser menor que as conferências já iniciadas.`,
        'warning'
      );
      return;
    }

    this.salvandoDistribuicao = true;
    this.spinner.show();
    try {
      const response = await this.requisicao(
        'put',
        `lotes-conferencia/${this.loteSelecionado.id}/distribuicao`,
        {
          distribuicoes: this.distribuicao.conferidores
            .filter(item => item.disponivel)
            .map(item => ({
              id_conferidor: item.id_conferidor,
              quantidade: Number(item.quantidade_planejada)
            }))
        }
      );
      this.distribuicao = response.data;
      modal.close('Distribuição salva');
      await Promise.all([this.carregarLotes(), this.carregarConferencias()]);
      await this.app.alert('Distribuição salva', 'Os servidores pendentes foram distribuídos entre os conferidores.', 'success');
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível salvar a distribuição.', 'error');
    } finally {
      this.salvandoDistribuicao = false;
      this.spinner.hide();
      this.cdr.detectChanges();
    }
  }

  async usarFilaLivre(modal: any) {
    const confirmou = await this.app.confirm(
      'Usar fila livre',
      'Os servidores pendentes serão liberados para todos os conferidores. Conferências iniciadas ou concluídas manterão seus responsáveis.',
      'Ativar fila livre'
    );
    if (!confirmou) return;

    this.removendoDistribuicao = true;
    this.spinner.show();
    try {
      const response = await this.requisicao(
        'delete',
        `lotes-conferencia/${this.loteSelecionado.id}/distribuicao`
      );
      this.distribuicao = response.data;
      modal.close('Fila livre ativada');
      await Promise.all([this.carregarLotes(), this.carregarConferencias()]);
      await this.app.alert(
        'Fila livre ativada',
        'Os servidores pendentes sem responsável agora estão disponíveis para todos os conferidores.',
        'success'
      );
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível ativar a fila livre.', 'error');
    } finally {
      this.removendoDistribuicao = false;
      this.spinner.hide();
      this.cdr.detectChanges();
    }
  }

  async resetarConferencia(item: any) {
    const motivo = await this.app.solicitarMotivo(
      'Reabrir conferência',
      `${item.nome_servidor} voltará para a situação pendente. Informe ao conferidor o motivo da reconferência.`,
      'Reabrir conferência',
      'Informe o motivo da reconferência',
      'O motivo da reconferência é obrigatório.'
    );
    if (!motivo) return;

    this.spinner.show();
    try {
      await this.requisicao(
        'post',
        `lotes-conferencia/${this.loteSelecionado.id}/servidores/${item.id_servidor}/resetar`,
        { motivo }
      );
      await Promise.all([this.carregarLotes(), this.carregarConferencias(), this.carregarResumoConferidores()]);
      await this.app.alert('Conferência resetada', 'O servidor está disponível para reconferência.', 'success');
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível resetar a conferência.', 'error');
    } finally {
      this.spinner.hide();
      this.cdr.detectChanges();
    }
  }

  async fecharLote() {
    if (!this.loteSelecionado || this.loteSelecionado.status !== 'aberto') return;
    const confirmou = await this.app.confirm(
      'Fechar lote',
      'Após o fechamento, novas conferências e resets não serão permitidos neste lote.',
      'Fechar e gerar PDF'
    );
    if (!confirmou) return;

    this.spinner.show();
    try {
      const response = await this.requisicao(
        'post',
        `lotes-conferencia/${this.loteSelecionado.id}/fechar`,
        {}
      );
      this.loteSelecionado = response.data;
      await this.carregarLotes();
      await this.gerarPdf();
      await this.app.alert('Lote fechado', 'O relatório foi gerado e o lote está somente para consulta.', 'success');
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível fechar o lote.', 'error');
    } finally {
      this.spinner.hide();
      this.cdr.detectChanges();
    }
  }

  async gerarPdf() {
    if (!this.loteSelecionado) return;
    try {
      const relatorio = await this.requisicao(
        'get',
        `lotes-conferencia/${this.loteSelecionado.id}/relatorio`
      );
      const lote = relatorio.lote;
      const linhas = (relatorio.por_conferidor || []).map(item => [
        item.nome_conferidor,
        Number(item.total || 0),
        Number(item.conferidas || 0),
        Number(item.em_conferencia || 0),
        Number(item.pendentes || 0)
      ]);

      const docDefinition: any = {
        pageSize: 'A4',
        pageMargins: [38, 44, 38, 44],
        content: [
          { text: 'Relatório do lote de conferência', style: 'title' },
          { text: `${lote.nome_cidade} · Lote ${lote.versao}`, style: 'subtitle' },
          {
            columns: [
              { text: `Nome: ${lote.nome}\nSituação: ${this.rotuloStatus(lote.status)}` },
              { text: `Abertura: ${this.formatarData(lote.data_abertura)}\nFechamento: ${this.formatarData(lote.data_fechamento)}`, alignment: 'right' }
            ],
            margin: [0, 14, 0, 14]
          },
          {
            table: {
              widths: ['*', '*', '*', '*'],
              body: [
                ['Total', 'Conferidas', 'Em conferência', 'Pendentes'],
                [
                  Number(lote.total_conferencias || 0),
                  Number(lote.conferidas || 0),
                  Number(lote.em_conferencia || 0),
                  Number(lote.pendentes || 0)
                ]
              ]
            },
            layout: 'lightHorizontalLines',
            margin: [0, 0, 0, 18]
          },
          { text: 'Resultado por conferidor', style: 'section' },
          {
            table: {
              headerRows: 1,
              widths: ['*', 55, 65, 80, 55],
              body: [['Conferidor', 'Total', 'Conferidas', 'Em conferência', 'Pendentes'], ...linhas]
            },
            layout: 'lightHorizontalLines'
          },
          {
            text: `Gerado em ${this.formatarData(relatorio.gerado_em)}`,
            style: 'footer'
          }
        ],
        styles: {
          title: { fontSize: 20, bold: true, color: '#295e5b' },
          subtitle: { fontSize: 13, color: '#4e7977', margin: [0, 4, 0, 0] },
          section: { fontSize: 13, bold: true, margin: [0, 0, 0, 8] },
          footer: { fontSize: 8, color: '#777777', margin: [0, 20, 0, 0] }
        },
        defaultStyle: { fontSize: 9 }
      };

      const nomeArquivo = `lote-conferencia-${lote.nome_cidade}-v${lote.versao}.pdf`
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9.-]+/g, '-').toLowerCase();
      pdfMake.createPdf(docDefinition).download(nomeArquivo);
    } catch (error) {
      await this.app.alert('Erro', error.message || 'Não foi possível gerar o PDF.', 'error');
    }
  }

  formatarData(valor: any): string {
    if (!valor) return '-';
    return new Date(valor).toLocaleString('pt-BR');
  }

  rotuloStatus(status: string): string {
    return status === 'aberto' ? 'Aberto' : 'Fechado';
  }

  rotuloModo(modo: string): string {
    return modo === 'POR_META' ? 'Por distribuição' : 'Fila livre';
  }

  rotuloSituacao(situacao: string): string {
    const rotulos = {
      pendente: 'Pendente',
      em_conferencia: 'Em conferência',
      conferido: 'Conferido',
      cancelado: 'Cancelado'
    };
    return rotulos[situacao] || situacao || '-';
  }
}

